import React, { useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Button from '../../Button';
import useForm from '../../../utils/useForm';
import TogglePasswordVisibilityButton from '../../forms/TogglePasswordVisibilityButton';
import ConfirmAction from '../../dialogs/ConfirmAction';
import { notifySuccess, notifyError } from '../../../utils/notify';

const PatientEngagementAccountManagement = ({
  recipient: { pepUserName, pepEmail },
  sendResetPepLink,
  deletePEPUser,
  sendUserNamePEP,
}) => {
  const { t } = useTranslation();
  const usernameInputRef = useRef();
  const emailInputRef = useRef();

  const { renderField } = useForm({
    inputs: {
      username: {
        ref: usernameInputRef,
        label: t('forms:username.label'),
        type: 'password',
        defaultValue: pepUserName,
        readOnly: true,
        suffix: <TogglePasswordVisibilityButton inputRef={usernameInputRef} />,
        schema: {
          type: 'string',
          optional: true,
        },
      },
      email: {
        ref: emailInputRef,
        label: t('forms:email.label'),
        type: 'password',
        defaultValue: pepEmail,
        readOnly: true,
        suffix: <TogglePasswordVisibilityButton inputRef={emailInputRef} />,
        schema: {
          type: 'string',
          optional: true,
        },
      },
    },
  });

  const [isSending, setIsSending] = useState(false);
  const [isResetPassword, setResetPassword] = useState(true);
  const [isUserNameSent, setIsUserNameSent] = useState(false);
  const message = useMemo(() => {
    if (isResetPassword) {
      return 'Are you sure you want to send a password reset email?';
    }
    if (isUserNameSent) {
      return 'Are you sure you want to send username?';
    }
    return 'Are you sure you want to delete this user?';
  }, [isResetPassword, isUserNameSent]);
  const [
    isConfirmSendPasswordResetEmailVisible,
    setIsConfirmSendPasswordResetEmailVisible,
  ] = useState(false);
  const handleOnOpenSendPasswordResetEmailDialog = () => {
    setResetPassword(true);
    setIsUserNameSent(false);
    setIsConfirmSendPasswordResetEmailVisible(true);
  };
  const handleOnOpenDeleteUserDialog = () => {
    setResetPassword(false);
    setIsUserNameSent(false);
    setIsConfirmSendPasswordResetEmailVisible(true);
  };
  const handleOnOpenSendUserNameDialog = () => {
    setResetPassword(false);
    setIsUserNameSent(true);
    setIsConfirmSendPasswordResetEmailVisible(true);
  };
  const handleOnCloseSendPasswordResetEmailDialog = () =>
    setIsConfirmSendPasswordResetEmailVisible(false);
  const handleOnSendPasswordResetEmail = () => {
    setIsSending(true);
    if (isResetPassword) {
      sendResetPepLink()
        .then(() => {
          setIsSending(false);
          handleOnCloseSendPasswordResetEmailDialog();
          notifySuccess('Password Reset Email Sent')();
        })
        .catch(() => {
          setIsSending(false);
          handleOnCloseSendPasswordResetEmailDialog();
          notifyError()('Failed to send password reset email');
        });
    }
    if (isUserNameSent) {
      sendUserNamePEP()
        .then(() => {
          setIsSending(false);
          handleOnCloseSendPasswordResetEmailDialog();
          notifySuccess('User Name Sent')();
        })
        .catch(() => {
          setIsSending(false);
          handleOnCloseSendPasswordResetEmailDialog();
          notifyError()('Failed to send user name');
        });
    }
    if (!isResetPassword && !isUserNameSent) {
      deletePEPUser()
        .then(() => {
          setIsSending(false);
          handleOnCloseSendPasswordResetEmailDialog();
          notifySuccess('User Deleted')();
        })
        .catch(() => {
          setIsSending(false);
          handleOnCloseSendPasswordResetEmailDialog();
          notifyError()('Failed to delete user');
        });
    }
  };

  return (
    <div className="stack-6">
      <h2>User Account Management</h2>
      <div className="grid grid-cols-5 gap-5 items-end">
        {renderField('username')}
        {renderField('email')}
        <Button onClick={handleOnOpenDeleteUserDialog} disabled={!pepUserName}>
          Delete User
        </Button>
        <Button
          onClick={handleOnOpenSendPasswordResetEmailDialog}
          disabled={!pepUserName}
        >
          Send Password Reset Email
        </Button>
        <Button
          onClick={handleOnOpenSendUserNameDialog}
          disabled={!pepUserName}
        >
          Send User Name
        </Button>
        <ConfirmAction
          message={message}
          open={isConfirmSendPasswordResetEmailVisible}
          onConfirm={handleOnSendPasswordResetEmail}
          onCancel={handleOnCloseSendPasswordResetEmailDialog}
          loading={isSending}
        />
      </div>
    </div>
  );
};

PatientEngagementAccountManagement.propTypes = {
  recipient: PropTypes.shape({
    pepUserName: PropTypes.string,
    pepEmail: PropTypes.string,
  }).isRequired,
  sendResetPepLink: PropTypes.func.isRequired,
  deletePEPUser: PropTypes.func.isRequired,
  sendUserNamePEP: PropTypes.func.isRequired,
};

export default PatientEngagementAccountManagement;
